import React, { useState, useEffect } from 'react';
import {
  Route,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  ArrowRight,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { outboundRulesAPI, sipTrunksAPI } from '../services/api';

const OutboundRules = () => {
  const [outboundRules, setOutboundRules] = useState([]);
  const [sipTrunks, setSipTrunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '',
    pattern: '',
    prefix: '',
    trunk_id: '',
    strip_digits: 0,
    prepend: '',
    priority: 1,
    enabled: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesRes, trunksRes] = await Promise.all([
        outboundRulesAPI.list(),
        sipTrunksAPI.list()
      ]);
      setOutboundRules(rulesRes.data);
      setSipTrunks(trunksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRules = outboundRules
    .filter((rule) =>
      rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rule.pattern.includes(searchQuery)
    )
    .sort((a, b) => a.priority - b.priority);

  const getTrunkName = (trunkId) => {
    const trunk = sipTrunks.find((t) => t.id === trunkId);
    return trunk ? trunk.name : trunkId;
  };

  const handleAddRule = async () => {
    try {
      await outboundRulesAPI.create(newRule);
      setIsAddModalOpen(false);
      setNewRule({
        name: '',
        pattern: '',
        prefix: '',
        trunk_id: '',
        strip_digits: 0,
        prepend: '',
        priority: 1,
        enabled: true
      });
      fetchData();
    } catch (error) {
      console.error('Error creating rule:', error);
    }
  };

  const handleEditRule = async () => {
    try {
      await outboundRulesAPI.update(selectedRule.id, {
        name: selectedRule.name,
        pattern: selectedRule.pattern,
        prefix: selectedRule.prefix,
        trunk_id: selectedRule.trunk_id,
        strip_digits: selectedRule.strip_digits,
        prepend: selectedRule.prepend,
        priority: selectedRule.priority,
        enabled: selectedRule.enabled
      });
      setIsEditModalOpen(false);
      setSelectedRule(null);
      fetchData();
    } catch (error) {
      console.error('Error updating rule:', error);
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      await outboundRulesAPI.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting rule:', error);
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await outboundRulesAPI.update(rule.id, { enabled: !rule.enabled });
      fetchData();
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Outbound Rules</h1>
          <p className="text-gray-400 mt-1">Configure dialing rules and trunk routing</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {/* Search */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search rules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1e1e2d]">
                  <th className="text-left p-4 text-sm font-medium text-gray-400 w-12">Priority</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Name</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Pattern</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Trunk</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Manipulation</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400 w-20">Enabled</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400 w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map((rule) => (
                  <tr
                    key={rule.id}
                    className={`border-b border-[#1e1e2d] hover:bg-[#1e1e2d]/50 transition-colors ${!rule.enabled ? 'opacity-50' : ''}`}
                  >
                    <td className="p-4">
                      <Badge variant="outline" className="border-[#2a2a3d] text-white">
                        {rule.priority}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Route className="w-4 h-4 text-blue-400" />
                        <span className="text-white font-medium">{rule.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <code className="text-purple-400 bg-purple-500/10 px-2 py-1 rounded">
                        {rule.pattern}
                      </code>
                    </td>
                    <td className="p-4">
                      <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                        {getTrunkName(rule.trunk_id)}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm">
                        {rule.strip_digits > 0 && (
                          <Badge variant="outline" className="border-red-500/30 text-red-400">
                            Strip: {rule.strip_digits}
                          </Badge>
                        )}
                        {rule.prepend && (
                          <Badge variant="outline" className="border-green-500/30 text-green-400">
                            Prepend: {rule.prepend}
                          </Badge>
                        )}
                        {!rule.strip_digits && !rule.prepend && (
                          <span className="text-gray-500">None</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={() => handleToggleRule(rule)}
                      />
                    </td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1e1e2d] border-[#2a2a3d]">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedRule(rule);
                              setIsEditModalOpen(true);
                            }}
                            className="text-gray-300 hover:text-white hover:bg-[#2a2a3d] cursor-pointer"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteRule(rule.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRules.length === 0 && (
            <div className="p-12 text-center">
              <Route className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No outbound rules found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pattern Reference */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-4">Pattern Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-[#1e1e2d] rounded-lg">
              <code className="text-purple-400">X</code>
              <span className="text-gray-400 ml-2">= Any digit 0-9</span>
            </div>
            <div className="p-3 bg-[#1e1e2d] rounded-lg">
              <code className="text-purple-400">N</code>
              <span className="text-gray-400 ml-2">= Any digit 2-9</span>
            </div>
            <div className="p-3 bg-[#1e1e2d] rounded-lg">
              <code className="text-purple-400">.</code>
              <span className="text-gray-400 ml-2">= One or more digits</span>
            </div>
            <div className="p-3 bg-[#1e1e2d] rounded-lg">
              <code className="text-purple-400">[abc]</code>
              <span className="text-gray-400 ml-2">= a, b, or c</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Rule Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Add Outbound Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Rule Name</Label>
                <Input
                  value={newRule.name}
                  onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                  placeholder="Local Calls"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Priority</Label>
                <Input
                  type="number"
                  value={newRule.priority}
                  onChange={(e) => setNewRule({ ...newRule, priority: parseInt(e.target.value) })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Pattern</Label>
              <Input
                value={newRule.pattern}
                onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })}
                placeholder="9NXXXXXX"
                className="bg-[#1e1e2d] border-[#2a2a3d] text-white font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Route to Trunk</Label>
              <Select
                value={newRule.trunk_id}
                onValueChange={(value) => setNewRule({ ...newRule, trunk_id: value })}
              >
                <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                  <SelectValue placeholder="Select trunk" />
                </SelectTrigger>
                <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                  {sipTrunks.map((trunk) => (
                    <SelectItem key={trunk.id} value={trunk.id}>
                      {trunk.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Strip Digits</Label>
                <Input
                  type="number"
                  value={newRule.strip_digits}
                  onChange={(e) => setNewRule({ ...newRule, strip_digits: parseInt(e.target.value) })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Prepend</Label>
                <Input
                  value={newRule.prepend}
                  onChange={(e) => setNewRule({ ...newRule, prepend: e.target.value })}
                  placeholder="+1"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Enabled</Label>
              <Switch
                checked={newRule.enabled}
                onCheckedChange={(checked) => setNewRule({ ...newRule, enabled: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddRule} className="bg-blue-600 hover:bg-blue-700">
              Add Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Outbound Rule</DialogTitle>
          </DialogHeader>
          {selectedRule && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Rule Name</Label>
                  <Input
                    value={selectedRule.name}
                    onChange={(e) => setSelectedRule({ ...selectedRule, name: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Priority</Label>
                  <Input
                    type="number"
                    value={selectedRule.priority}
                    onChange={(e) => setSelectedRule({ ...selectedRule, priority: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Pattern</Label>
                <Input
                  value={selectedRule.pattern}
                  onChange={(e) => setSelectedRule({ ...selectedRule, pattern: e.target.value })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Route to Trunk</Label>
                <Select
                  value={selectedRule.trunk_id}
                  onValueChange={(value) => setSelectedRule({ ...selectedRule, trunk_id: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    {sipTrunks.map((trunk) => (
                      <SelectItem key={trunk.id} value={trunk.id}>
                        {trunk.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Strip Digits</Label>
                  <Input
                    type="number"
                    value={selectedRule.strip_digits}
                    onChange={(e) => setSelectedRule({ ...selectedRule, strip_digits: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Prepend</Label>
                  <Input
                    value={selectedRule.prepend}
                    onChange={(e) => setSelectedRule({ ...selectedRule, prepend: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-gray-400">Enabled</Label>
                <Switch
                  checked={selectedRule.enabled}
                  onCheckedChange={(checked) => setSelectedRule({ ...selectedRule, enabled: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleEditRule} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutboundRules;
